import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import TitleSetter from '../../../components/TitleSetter';
import TopBar from '../../../components/TopBar';
import SettingsLayout from '../components/Layout/SettingsLayout';
import SettingsNav from '../components/Layout/SettingsNav';
import BetaBanner from '../components/Common/BetaBanner';
import ProfileTab from '../components/Profile/ProfileTab';
import TeamTab from '../components/Team/TeamTab';
import OwnerTab from '../components/Owner/OwnerTab';
import SupportTab from '../components/Support/SupportTab';
import StatsConfigure from '../../../components/StatsConfigure';
import '../css/SettingsBase.css';
import useProfileSettings from '../hooks/useProfileSettings';
import useTeamAccess from '../hooks/useTeamAccess';
import useOwnerTools from '../hooks/useOwnerTools';
import useSupportTickets from '../hooks/useSupportTickets';
import { canAccessDatabaseConfig as canAccessDatabaseConfigByRole } from '../../../utils/teamRole';

const SettingsView = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [userProfile, setUserProfile] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const profileState = useProfileSettings(auth, db);
  const {
    profile,
    teamId,
    birthdate,
    setBirthdate,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    ign,
    setIgn,
    role,
    setRole,
    saving,
    status,
    saveProfile,
  } = profileState;

  const [isOwner, setIsOwner] = useState(false);
  const [ownerUid, setOwnerUid] = useState('');
  const canAccessDatabaseConfig = canAccessDatabaseConfigByRole(role);

  useEffect(() => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    const validTabs = new Set([
      'profile',
      'team',
      'database-config',
      'owner',
      'support',
    ]);

    if (!tabParam || !validTabs.has(tabParam)) return;
    if (tabParam === 'owner' && !isOwner) return;
    if (tabParam === 'database-config' && !canAccessDatabaseConfig) return;
    setActiveTab(tabParam);
  }, [location.search, isOwner, canAccessDatabaseConfig]);

  useEffect(() => {
    if (activeTab === 'database-config' && !canAccessDatabaseConfig) {
      setActiveTab('profile');
    }
  }, [activeTab, canAccessDatabaseConfig]);

  // Owner flag
  useEffect(() => {
    const loadTeamOwner = async () => {
      const user = auth?.currentUser;
      if (!user || !teamId) {
        setIsOwner(false);
        setOwnerUid('');
        return;
      }
      try {
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        if (teamSnap.exists()) {
          const data = teamSnap.data();
          const owner = data.owner || data.ownerId || '';
          setOwnerUid(owner);
          setIsOwner(owner === user.uid);
        } else {
          setIsOwner(false);
          setOwnerUid('');
        }
      } catch (err) {
        console.error('Failed to load team owner', err);
        setIsOwner(false);
        setOwnerUid('');
      }
    };
    loadTeamOwner();
  }, [teamId]);

  const teamAccess = useTeamAccess(auth, db, teamId, isOwner);
  const ownerTools = useOwnerTools(auth, db, teamId, isOwner, ownerUid, teamAccess.setTeamMembers);
  const supportTickets = useSupportTickets(auth, db, profile);

  // keep TopBar profile in sync
  useEffect(() => {
    if (profile) setUserProfile(profile);
  }, [profile]);

  return (
    <>
      <TitleSetter title="Settings" />
      <TopBar
        userProfile={userProfile}
        setUserProfile={setUserProfile}
        teamLogo={teamLogo}
        setTeamLogo={setTeamLogo}
      />
      <div className="settings-root">
        <SettingsLayout
          nav={
            <SettingsNav
              activeTab={activeTab}
              onChange={setActiveTab}
              isOwner={isOwner}
              canAccessDatabaseConfig={canAccessDatabaseConfig}
            />
          }
        >
          <BetaBanner />
          {activeTab === 'profile' && (
            <ProfileTab
              firstName={firstName}
              lastName={lastName}
              ign={ign}
              birthdate={birthdate}
              role={role}
              profile={profile}
              saving={saving}
              status={status}
              setFirstName={setFirstName}
              setLastName={setLastName}
              setIgn={setIgn}
              setBirthdate={setBirthdate}
              setRole={setRole}
              saveProfile={saveProfile}
              auth={auth}
            />
          )}

          {activeTab === 'team' && (
            <TeamTab
              teamId={teamId}
              teamMeta={teamAccess.teamMeta}
              role={role}
              directoryStatus={teamAccess.directoryStatus}
              teamMembers={teamAccess.teamMembers}
              teamMembersLoading={teamAccess.teamMembersLoading}
              teamMembersError={teamAccess.teamMembersError}
              navigate={navigate}
              location={location}
            />
          )}

          {activeTab === 'database-config' && teamId && canAccessDatabaseConfig && (
            <StatsConfigure teamId={teamId} />
          )}

          {activeTab === 'owner' && isOwner && (
            <OwnerTab
              teamId={teamId}
              ownerUid={ownerUid}
              directoryStatus={teamAccess.directoryStatus}
              joinRequests={ownerTools.joinRequests}
              joinRequestsLoading={ownerTools.joinRequestsLoading}
              joinRequestsError={ownerTools.joinRequestsError}
              updateJoinRequestStatus={ownerTools.updateJoinRequestStatus}
              updatingRequestId={ownerTools.updatingRequestId}
              teamMembers={teamAccess.teamMembers}
              teamMembersLoading={teamAccess.teamMembersLoading}
              teamMembersError={teamAccess.teamMembersError}
              removeMemberFromTeam={ownerTools.removeMemberFromTeam}
              updateMemberRole={ownerTools.updateMemberRole}
            />
          )}

          {activeTab === 'support' && (
            <SupportTab
              newTicketSubject={supportTickets.newTicketSubject}
              setNewTicketSubject={supportTickets.setNewTicketSubject}
              newTicketBody={supportTickets.newTicketBody}
              setNewTicketBody={supportTickets.setNewTicketBody}
              createTicket={supportTickets.createTicket}
              tickets={supportTickets.tickets}
              ticketsLoading={supportTickets.ticketsLoading}
              openTicket={supportTickets.openTicket}
              selectedTicket={supportTickets.selectedTicket}
              ticketMessages={supportTickets.ticketMessages}
              messageLoading={supportTickets.messageLoading}
              replyText={supportTickets.replyText}
              setReplyText={supportTickets.setReplyText}
              sendReply={supportTickets.sendReply}
              ticketStatus={supportTickets.ticketStatus}
              setTicketStatus={supportTickets.setTicketStatus}
              auth={auth}
            />
          )}
        </SettingsLayout>
      </div>
    </>
  );
};

export default SettingsView;
